import { useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
} from '@product-framework/index';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceJobFormCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'servicejobformFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/service-job-forms`;

  const preProcessRequestData = data => {
    const {
      createdByUserId,
      lastModifiedByUserId,
      formUiSchema,
      checkOrder,
      ...other
    } = data;
    return {
      formUiSchema: formUiSchema ? JSON.parse(formUiSchema) : {},
      checkOrder: parseInt(data.checkOrder, 10) || 1,
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEJOBFORM_CREATE_SUCCESS',
          defaultMessage: 'Job Form was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/servicejobforms/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEJOBFORM_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the job form',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBFORMS_BREADCRUMB',
        defaultMessage: 'Job Forms',
      }),
      icon: IconLibrary.ServiceJobForms,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/servicejobforms');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_SERVICEJOBFORM_BREADCRUMB',
        defaultMessage: 'Create Job Form',
      }),
      icon: IconLibrary.ServiceJobForm,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_SERVICEJOBFORMS_STACKABLE_BUTTON',
        defaultMessage: 'List Job Forms',
      }),
      Icon: IconLibrary.ServiceJobForms,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/servicejobforms');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'SERVICEJOBFORM_CREATE_HEADING',
          defaultMessage: 'Create Job Form',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <Button
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'SERVICEJOBFORM_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Job Form',
                })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'SERVICEJOBFORM_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </Button>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobFormCreate);
